import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { playerIdAtom } from '@/atoms/player/player';
import { playerAiSummaryFilterParamsAtom } from '@/atoms/queries/player/playerAiSummaryFilters';
import { unwrappedSelectedSummaryProfileAtom } from '@/atoms/radar';
import { isNullish } from '@/utils/predicates';
import { toProfileInfo } from '@/utils/radar';
import { buildPlayerData } from '@/utils/summary';
import { usePlayerFilteredSummaryData } from './usePlayerFilteredSummaryData';
import { useSummaryPositionGroup } from './useSummaryPositionGroup';

const unwrappedPlayerAiSummaryFilterParamsAtom = unwrap(playerAiSummaryFilterParamsAtom, () => undefined);

/**
 * The AI summary's playerData for the selected player and profile, shared by the player dashboard and
 * the scout preview so the two can't drift. `undefined` while anything is still resolving (including
 * `isPending`, for a caller's own gate such as the preview's URL sync), so callers render a loader;
 * throws when a resolved input is unusable (no matching cycles, no matching profile, no player).
 */
export const useSummaryPlayerData = ({ isPending = false }: { isPending?: boolean } = {}) => {
  const { t } = useTranslation('radar');
  const playerId = useAtomValue(playerIdAtom);
  const filterParams = useAtomValue(unwrappedPlayerAiSummaryFilterParamsAtom);
  const summaryData = usePlayerFilteredSummaryData();
  const selectedProfile = useAtomValue(unwrappedSelectedSummaryProfileAtom);
  const summaryPositionGroup = useSummaryPositionGroup(selectedProfile, summaryData?.primaryPosition);

  // `selectedProfile` is undefined while the profile atom resolves, and null if it resolved but no
  // template matched the selected id — the same undefined/null distinction filterParams uses below.
  if (isPending || filterParams === undefined || summaryData === undefined || selectedProfile === undefined) {
    return undefined;
  }
  if (!filterParams)
    throw new Error('playerAiSummaryFilterParamsAtom resolved to null: no player cycles match selected filters');
  if (!selectedProfile)
    throw new Error('selectedSummaryProfileAtom resolved to null: no summary profile matches the selected id');
  if (isNullish(playerId)) throw new Error('playerIdAtom resolved to null: cannot render summary without a player id');
  // The group waits on the radar templates, so a loaded profile can still be pending here.
  if (!summaryPositionGroup) return undefined;

  const profileInfo = toProfileInfo(
    selectedProfile,
    t,
    summaryData.positionMinutes,
    summaryData.hiddenMetricKeys,
    summaryPositionGroup,
  );
  return buildPlayerData(playerId, summaryData, filterParams, profileInfo);
};
